import { useRef, useState, useCallback, useEffect } from 'react';

type RecorderStatus =
  | 'idle'
  | 'acquiring_media'
  | 'recording'
  | 'stopping'
  | 'stopped'
  | 'error';

type UseVideoRecorderOptions = {
  /**
   * How often (ms) MediaRecorder.requestData() is called while recording.
   * This flushes buffered data and forces keyframe insertion, which prevents
   * the ~15 s video freeze in Safari (moov atom / keyframe interval bug).
   * Default: 1000 ms (1 s chunks).
   */
  timeslice?: number;
  onStop?: (blobUrl: string, blob: Blob) => void;
  onError?: (err: Error) => void;
};

type UseVideoRecorderReturn = {
  status: RecorderStatus;
  previewStream: MediaStream | null;
  startRecording: (stream: MediaStream) => void;
  stopRecording: () => void;
  clearBlobUrl: () => void;
};

/**
 * Drop-in replacement for the relevant parts of useReactMediaRecorder.
 *
 * Key differences from react-media-recorder:
 *  - Calls mediaRecorder.start(timeslice) so the browser writes incremental
 *    moof/mdat atoms (fragmented MP4) rather than a single moov-at-end blob.
 *    This makes the recorded video seekable and prevents the Safari freeze.
 *  - Accepts the caller-owned MediaStream directly (no internal getUserMedia).
 *  - Exposes previewStream so VideoRecorder can attach it to its <video>.
 *  - Waits for the post-stop burst to settle before assembling the blob, so the
 *    recording is reported exactly once and with every chunk. See the note on
 *    FINALIZE_SETTLE_MS below.
 */

/**
 * How long to wait for the dataavailable burst to finish before assembling.
 *
 * requestData() + stop() do not produce one final event: the browser answers
 * with several, and every one of them arrives *after* the recorder has already
 * gone inactive. Measured in Chrome 152 at the end of a take:
 *
 *   size=38200  state=inactive   <- the requestData flush
 *   size=18211  state=inactive   <- stop()'s final chunk
 *   size=0      state=inactive   <- terminator
 *
 * So "inactive" does not mean "finished". Assembling on the first of those
 * yields a truncated clip; assembling on each yields three blobs and three
 * object URLs, and revoking the superseded ones pulls the URL out from under a
 * <video> that is already showing it — playback then stays broken until the
 * page is remounted and re-reads the blob from the store.
 *
 * onstop is not a reliable terminator either: Safari can fire it before the
 * last chunk is delivered, which is why assembly was moved out of it in the
 * first place. So instead we finalise once the events stop arriving. The burst
 * spans single-digit milliseconds; 100ms is far outside it and imperceptible.
 */
const FINALIZE_SETTLE_MS = 100;
export function useVideoRecorder({
  timeslice = 1000,
  onStop,
  onError,
}: UseVideoRecorderOptions = {}): UseVideoRecorderReturn {
  const [status, setStatus] = useState<RecorderStatus>('idle');
  const [previewStream, setPreviewStream] = useState<MediaStream | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const blobUrlRef = useRef<string | null>(null);
  const settleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalizedRef = useRef(false);

  // A take that is still settling when the component goes away must not call
  // back into it.
  useEffect(
    () => () => {
      if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
    },
    [],
  );

  const startRecording = useCallback(
    (stream: MediaStream) => {
      if (mediaRecorderRef.current) return; // already recording

      chunksRef.current = [];
      finalizedRef.current = false;
      if (settleTimerRef.current) {
        clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }

      // Pick the best available MIME type.
      // Safari supports video/mp4; Chrome/Firefox prefer video/webm.
      const mimeType =
        [
          'video/mp4;codecs=avc1,mp4a.40.2',
          'video/mp4',
          'video/webm;codecs=vp9,opus',
          'video/webm;codecs=vp8,opus',
          'video/webm',
        ].find((t) => MediaRecorder.isTypeSupported(t)) ?? '';

      let mr: MediaRecorder;
      try {
        mr = new MediaRecorder(stream, mimeType ? { mimeType } : {});
      } catch (err) {
        const e = err instanceof Error ? err : new Error(String(err));
        setStatus('error');
        onError?.(e);
        return;
      }

      /** Build the recording from every chunk collected. Runs once per take. */
      const finalize = () => {
        if (finalizedRef.current) return;
        finalizedRef.current = true;
        settleTimerRef.current = null;

        // Only ever the *previous* take's URL: this runs once, so it can never
        // revoke the URL handed out for the recording being finalised now.
        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
        }

        const blob = new Blob(chunksRef.current, {
          type: mr.mimeType || mimeType || 'video/mp4',
        });
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;

        setStatus('stopped');
        setPreviewStream(null);
        onStop?.(url, blob);
      };

      /** Restarted by each event, so the last one in the burst wins. */
      const scheduleFinalize = () => {
        if (finalizedRef.current) return;
        if (settleTimerRef.current) clearTimeout(settleTimerRef.current);
        settleTimerRef.current = setTimeout(finalize, FINALIZE_SETTLE_MS);
      };

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) {
          chunksRef.current.push(e.data);
        }

        // "inactive" means the burst has begun, not that it has finished —
        // more chunks still follow. Wait for them.
        if (mr.state === 'inactive') scheduleFinalize();
      };

      mr.onstop = () => scheduleFinalize();

      mr.onerror = (e) => {
        setStatus('error');
        onError?.(new Error(`MediaRecorder error: ${e.type}`));
      };

      mediaRecorderRef.current = mr;
      setPreviewStream(stream);
      setStatus('recording');

      // Pass timeslice so the browser emits ondataavailable chunks at regular
      // intervals. Each chunk boundary forces a keyframe and writes a seekable
      // fragment into the container — this is the core fix for the Safari freeze.
      mr.start(timeslice);
    },
    [timeslice, onStop, onError],
  );

  const stopRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (!mr || mr.state === 'inactive') return;

    setStatus('stopping');

    // requestData() forces the browser to emit one final ondataavailable event
    // with any remaining buffered data before the recorder transitions to
    // inactive. Without this, the last partial chunk can be silently dropped,
    // producing a truncated recording — especially on Safari under load.
    mr.requestData();
    mr.stop();
    mediaRecorderRef.current = null;
  }, []);

  const clearBlobUrl = useCallback(() => {
    if (settleTimerRef.current) {
      clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    finalizedRef.current = false;
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    chunksRef.current = [];
    setStatus('idle');
  }, []);

  return {
    status,
    previewStream,
    startRecording,
    stopRecording,
    clearBlobUrl,
  };
}
